import { standardSchemaResolver } from '@hookform/resolvers/standard-schema'
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ErrorIcon from '@mui/icons-material/Error'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Alert, AlertTitle, Box, Stack, Typography } from '@mui/material'
import { forgotPasswordConfirmFrontBodySchema } from '@shared/dto/auth.dto'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import FundingLogos from '../../components/common/FundingLogos'
import { PasswordInput } from '../../components/forms/PasswordInput'
import { LoaderButton } from '../../components/LoaderButton'
import { useForgotPasswordConfirmMutation } from '../../hooks/useMutations/useAuthMutations'
import { hasCombination, hasMinLength } from '../../utils/validation.utils'

type AlertType = 'success' | 'error'

export default function ForgotPasswordConfirm() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [alertType, setAlertType] = useState<AlertType>('success')
  const [showAlert, setShowAlert] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<{
    newPassword: string
    newPasswordConfirm: string
  }>({
    defaultValues: {
      newPassword: '',
      newPasswordConfirm: ''
    },
    resolver: standardSchemaResolver(forgotPasswordConfirmFrontBodySchema)
  })

  useEffect(() => {
    if (!searchParams.get('email') || !searchParams.get('code')) {
      navigate('/forgot-password')
    }
  }, [searchParams, navigate])

  const forgotPasswordConfirmMutation = useForgotPasswordConfirmMutation()

  const onSubmit = (data: { newPassword: string }) => {
    forgotPasswordConfirmMutation.mutate(
      {
        email: searchParams.get('email') as string,
        code: searchParams.get('code') as string,
        newPassword: data.newPassword
      },
      {
        onSuccess: () => {
          setAlertType('success')
          setShowAlert(true)
          setTimeout(() => {
            navigate('/sign-in')
          }, 2000)
        },
        onError: () => {
          setAlertType('error')
          setShowAlert(true)
        }
      }
    )
  }

  const password = watch('newPassword')
  const passwordConfirm = watch('newPasswordConfirm')

  const isMinLengthValid = hasMinLength(password)
  const isCombinationValid = hasCombination(password)
  const areEquals = password === passwordConfirm
  const isDisabled =
    !password ||
    !passwordConfirm ||
    !isMinLengthValid ||
    !isCombinationValid ||
    !areEquals ||
    forgotPasswordConfirmMutation.isPending

  return (
    <Stack
      direction='column'
      spacing='1.5rem'
      sx={{
        width: '37.875rem',
        minHeight: '43.6875rem',
        padding: '2.5rem',
        borderRadius: '1.5rem',
        backgroundColor: 'background.default'
      }}
    >
      <Typography variant='h4'>{t('forgot_password_confirm.title')}</Typography>
      <Typography variant='h6' color='text.secondary'>
        {t('forgot_password_confirm.subtitle')}
      </Typography>
      <PasswordInput
        name='newPassword'
        register={register}
        errors={errors}
        label={t('forgot_password_confirm.label')}
      />
      <PasswordInput
        name='newPasswordConfirm'
        register={register}
        errors={errors}
        label={t('forgot_password_confirm.label_repeat')}
      />
      <Box display='flex' alignItems='center' gap='0.5rem'>
        {password.length === 0 ? (
          <InfoOutlinedIcon color='info' />
        ) : areEquals ? (
          <CheckCircleIcon color='success' />
        ) : (
          <ErrorIcon color='error' />
        )}
        <Typography variant='body1'>{t('password.match')}</Typography>
      </Box>
      <Box display='flex' alignItems='center' gap='0.5rem'>
        {password.length === 0 ? (
          <InfoOutlinedIcon color='info' />
        ) : isMinLengthValid ? (
          <CheckCircleIcon color='success' />
        ) : (
          <ErrorIcon color='error' />
        )}
        <Typography variant='body1'>{t('password.length')}</Typography>
      </Box>
      <Box display='flex' alignItems='center' gap='0.5rem'>
        {password.length === 0 ? (
          <InfoOutlinedIcon color='info' />
        ) : isCombinationValid ? (
          <CheckCircleIcon color='success' />
        ) : (
          <ErrorIcon color='error' />
        )}
        <Typography variant='body1' color='text.primary'>
          {t('password.combination')}
        </Typography>
      </Box>
      <LoaderButton
        onClick={handleSubmit(onSubmit)}
        disabled={isDisabled}
        loading={forgotPasswordConfirmMutation.isPending}
        startIcon={<ChevronLeftIcon />}
        label={
          forgotPasswordConfirmMutation.isPending
            ? t('forgot_password_confirm.button_loading')
            : t('forgot_password_confirm.button')
        }
        sx={{ textTransform: 'none', padding: '0.5rem 2rem' }}
      />
      <Alert
        severity={alertType}
        sx={{
          visibility: showAlert ? 'visible' : 'hidden'
        }}
      >
        {alertType === 'error' && (
          <AlertTitle>{t('forgot_password_confirm.error.title')}</AlertTitle>
        )}
        {alertType === 'success'
          ? t('forgot_password_confirm.success')
          : t('forgot_password_confirm.error.description')}
      </Alert>
      <FundingLogos size='small' sx={{ mt: '1.5rem' }} />
    </Stack>
  )
}
